import { useQueryClient } from "@tanstack/react-query";
import { TokenDTo } from "@/services/auth/type";
import { MerchantUserInformationDto } from "@/services/merchant/type";
import { useLocalStorage } from "@/utilities/helper/useLocalStorage";
import { useMemo, createContext, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

type TAuthProviderProps = {
  children: React.ReactNode;
};

type TokenDecode = {
  userId: string;
  phoneNumber: string;
  parentUserId: string;
  role: string;
  exp: number;
  iss: string;
  aud: string;
};

type AuthContext = {
  token: TokenDTo | null;
  login: (
    user: TokenDTo,
    route?: string,
    merchant?: MerchantUserInformationDto
  ) => void;
  logout: () => void;
  getUserByToken: () => TokenDecode | null;
};

export const AuthContext = createContext<AuthContext>({
  token: null,
  login: () => null,
  logout: () => null,
  getUserByToken: () => null,
});

export const AuthProvider = ({ children }: TAuthProviderProps) => {
  const [token, setToken] = useLocalStorage("token", null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const login = useCallback(
    async (
      data: TokenDTo,
      route = "/dashboard",
      merchant?: MerchantUserInformationDto
    ) => {
      queryClient.clear();
      setToken(data);
      if (merchant) {
        navigate(`/auth/welcome`, {
          state: {
            merchant,
          },
        });
        // localStorage.setItem("merchant", JSON.stringify(merchant));
      } else {
        navigate(route);
      }
    },
    [navigate, setToken, queryClient]
  );

  const getUserByToken = useCallback(() => {
    return jwtDecode<TokenDecode>(token?.accessToken);
  }, [token]);

  const logout = useCallback(() => {
    const platform = localStorage.getItem("platform");
    queryClient.clear();
    setToken(null);
    window.localStorage.clear();
    if (platform) {
      navigate("/auth/login?platform=android", { replace: true });
    } else {
      navigate("/auth/login", { replace: true });
    }
    // if (platform) {
    //   const event = new Event("logout");
    //   document.dispatchEvent(event);
    // } else {
    //   navigate("/auth/login", { replace: true });
    // }
  }, [navigate, setToken, queryClient]);

  const value = useMemo(
    () => ({
      token,
      login,
      logout,
      getUserByToken,
    }),
    [token]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
